import Head from 'next/head';
import { useRouter } from 'next/router';
import { getKeywordsForPage, formatKeywords, type KeywordGroup } from '../../data/keywords';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  noindex?: boolean;
}

const defaultTitle = 'EPEN Ltd. | Video Plugin Development';
const defaultDescription =
  'Professional video plugin development for Adobe Premiere Pro & DaVinci Resolve. GPU-accelerated solutions with 25+ years expertise. Explore our plugins today!';
const defaultImage = 'https://vxfx.io/epenfav.webp';
const siteUrl = process.env.SITE_URL || 'https://vxfx.io';

// Helper function to truncate title to 50 characters (leaving room for " | EPEN Ltd.")
const truncateTitle = (title: string, maxLength: number = 50): string => {
  if (title.length <= maxLength) return title;
  return title.substring(0, maxLength).trim() + '...';
};

// Helper function to truncate description to 155 characters (optimal for search results)
const truncateDescription = (description: string, maxLength: number = 155): string => {
  if (description.length <= maxLength) return description;
  // Try to cut at a word boundary
  const truncated = description.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  if (lastSpace > maxLength - 20) {
    return truncated.substring(0, lastSpace).trim() + '...';
  }
  return truncated.trim() + '...';
};

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  noindex = false,
}) => {
  const router = useRouter();
  const currentUrl = url || `${siteUrl}${router.asPath}`;
  // Truncate title to ensure total length is under 60 characters
  const truncatedTitle = title ? truncateTitle(title, 50) : null;
  const pageTitle = truncatedTitle ? `${truncatedTitle} | EPEN Ltd.` : defaultTitle;
  // Truncate description to optimal length (155 characters)
  const rawDescription = description || defaultDescription;
  const pageDescription = truncateDescription(rawDescription, 155);
  const pageImage = image || defaultImage;

  // Get keywords from keyword mapping system if not provided
  const pageKeywordGroup = keywords ? null : getKeywordsForPage(router.asPath.split('?')[0]); // Remove query params
  const pageKeywords =
    keywords ||
    (pageKeywordGroup
      ? formatKeywords(pageKeywordGroup)
      : 'epen, video editing, adobe premiere, davinci resolve, plugin development');

  // Determine robots meta tag
  const isStaging = process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging';
  const robotsContent =
    noindex || isStaging || process.env.NODE_ENV !== 'production'
      ? 'noindex,nofollow'
      : 'index,follow';

  return (
    <Head>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={currentUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="EPEN Ltd." />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Article specific meta tags */}
      {type === 'article' && (
        <>
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {author && <meta property="article:author" content={author} />}
        </>
      )}

      {/* Additional meta tags */}
      <meta name="author" content="EPEN Ltd." />
      <meta name="language" content="English" />
      <meta name="geo.region" content="GB" />
      <meta name="geo.placename" content="United Kingdom" />
      
      {/* Mobile Viewport - Essential for responsive design and mobile SEO */}
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes"
      />
    </Head>
  );
};

export default SEO;
